import { UploadFilesModal, HiddenButton } from "../../../../../Constants/StyledComponents"
import ModalHeader from "../../../../../Components/ModalHeader/ModalHeader"
import { useUploadProcessProvider } from "../../../../../Providers/UploadProcessProvider"
import { ModalFooter } from "../../../../../Constants/StyledComponents"
import ModalScrollbar from "../../../../../Components/ModalScrollbar/ModalScrollbar"
import { UploadStepHeader } from "../../../../../Constants/StyledComponents";
import ModernMUITable from "../../../../../Components/ModernMUITable"
import { useAuthProvider } from "../../../../../Providers/AuthProvider"
import { useState, useEffect } from "react"
import { MobergButton } from "../../../../../Components/MobergButton/MobergButton";
import { MobergTheme } from "../../../../../Components/MobergThemes/MobergColors";
import { MobergButtonVariant } from "../../../../../Components/MobergButton/MobergButton";
import { MobergButtonShape } from "../../../../../Components/MobergButton/MobergButton";
import { useBackendLinksProvider } from "../../../../../Providers/BackendLinksProvider"
import { useEnvironmentVariablesProvider } from "../../../../../Providers/EnvironmentVariablesProvider"
import { useFetchTableData } from "../../../../../Hooks/useFetchTableData"


const projectColumns = [
	{ field: "identifier", flex: 0.4, headerName: "Project ID", minWidth: 90, visible: true },
	{ field: "name", flex: 0.4, headerName: "Name", minWidth: 70, visible: true },
	{ field: "bucket_name", flex: 0.4, headerName: "Repository", minWidth: 115, visible: true },
	{ field: "user_count", flex: 0.4, headerName: "No. users", valueGetter: (value, row) => row.user_count, minWidth: 110, visible: true },
	{ field: "patient_count", flex: 0.4, headerName: "No. patients", valueGetter: (value, row) => row.patient_count, minWidth: 110, visible: true },
]

const UploadProjectDetail = ({ setToNextWindow, setToPrevWindow }) => {
	const uploadProcessProvider = useUploadProcessProvider()
	const authProvider = useAuthProvider()
	const { LINKS } = useBackendLinksProvider()
	const environmentVariablesProvider = useEnvironmentVariablesProvider()

	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 })
	const [sortModel, setSortModel] = useState([{ field: 'identifier', sort: 'asc' }])

	const handleBack = () => {
		setToPrevWindow(true)
	}

	const handleNext = () => {
		setToNextWindow(true)
	}

	// Fetch current page's project data to support selection
	const { data: projectsData } = useFetchTableData({
		queryKey: "user_projects",
		endpoint: LINKS.DATA.UPLOAD.GET_USER_PROJECTS,
		body: { user_id: authProvider.currentUser.id },
		queryOptions: {},
		paginationModel,
		sortModel,
		supportsPageSize: true
	})

	const projectsById = (projectsData?.data || []).reduce((acc, project) => {
		acc[project.id] = project
		return acc
	}, {})

	const [selectedProjectRows, setSelectedProjectRows] = useState([])

	// Auto-select when there's only one project (check from total count)
	useEffect(() => {
		if (projectsData?.total === 1 && projectsData?.data?.length === 1) {
			// Auto-select the only project
			const onlyProject = projectsData.data[0]
			setSelectedProjectRows([onlyProject.id])
			uploadProcessProvider.setSelectedProjectID(onlyProject.identifier)
			uploadProcessProvider.setSelectedProjectPrimaryKey(onlyProject.id)
			uploadProcessProvider.setSelectedProjectName(onlyProject.name)
		} else {
			// Reset to previous selections if they exist, otherwise clear
			if (uploadProcessProvider?.selectedProjectPrimaryKey && uploadProcessProvider?.selectedProjectPrimaryKey !== '') {
				setSelectedProjectRows([uploadProcessProvider.selectedProjectPrimaryKey])
			} else {
				setSelectedProjectRows([])
			}
		}
	}, [projectsData, uploadProcessProvider])

	// Handle project selection change - with server-side pagination, get project info from current page data
	const handleProjectSelectionChange = (selectionArray) => {
		setSelectedProjectRows(selectionArray);
		
		if (selectionArray.length === 0) {
			// Clear selection
			uploadProcessProvider.setSelectedProjectID("");
			uploadProcessProvider.setSelectedProjectPrimaryKey("");
			uploadProcessProvider.setSelectedProjectName("");
		} else {
			// Get project from current page's data
			const selectedId = selectionArray[0];
			const selectedProject = projectsById[selectedId];
			
			if (selectedProject) {
				handleProjectCheckboxSelectionChange(selectedProject, true);
			}
		}
	}

	function handleProjectCheckboxSelectionChange(project, isClicked) {
		uploadProcessProvider.resetExistingTableData()
		if (project["identifier"] !== uploadProcessProvider.selectedProjectID) {
			if (!uploadProcessProvider.checkFoldersIsEmpty()) {
				if (window.confirm("Modifying existing project ID will remove your previously selected upload files. Do you want to continue?")) {
					uploadProcessProvider.resetTableData()
					uploadProcessProvider.resetUploadFolders()
					uploadProcessProvider.resetDirectoryNames()
				} else return
			}
		}
		uploadProcessProvider.setSelectedProjectID(isClicked ? project["identifier"] : "")
		uploadProcessProvider.setSelectedProjectPrimaryKey(isClicked ? project["id"] : "")
		uploadProcessProvider.setSelectedProjectName(isClicked ? project["name"] : "")
	}

	// Modal table toolbar props - minimal for upload workflow
	const uploadTableToolbarProps = {
		searchInput: { isShow: true },
		columnButton: { isShow: false }
	}

	// If siteTableData.length === 0 || projectTableData.length === 0
	// Return message that user needs at least one site and one project assigned to them before they can upload
	// Otherwise, proceed with upload process

	return (
		<>
			<UploadFilesModal id="uploadFilesButtonModal">
				<ModalHeader headerText="Select a Project" resetCache={uploadProcessProvider.resetCache} />
				<ModalScrollbar>
					<div style={{ marginBottom: "24px" }}>
						<UploadStepHeader>2. Select the project where the patient data will be stored</UploadStepHeader>
					</div>
					<ModernMUITable 
						columns={projectColumns} 
						queryKey="user_projects"
						endpoint={LINKS.DATA.UPLOAD.GET_USER_PROJECTS}
						body={{ user_id: authProvider.currentUser.id }}
						optionalParams={{}}
						paginationModel={paginationModel}
						onPaginationModelChange={setPaginationModel}
						sortModel={sortModel}
						onSortModelChange={setSortModel}
						paginationMode="server"
						sortingMode="server"
						enableRowSelection={true}
						checkboxSelection={true}
						supportsPageSize={true}
						onSelectionChange={handleProjectSelectionChange}
						rowSelectionModel={{ type: 'include', ids: new Set(selectedProjectRows) }}
						isRowSelectable={() => true}
						disableRowSelectionOnClick={false}
						disableSelectAll={true}
						showToolbar={true}
						height="90%"
						style={{ padding: '10px' }}
					/>
				</ModalScrollbar>

				<hr style={{ border: "1px solid var(--color-border-default)", width: "100%", margin: '0px auto' }} />
				<ModalFooter>
					<MobergButton
						theme={MobergTheme.BLUE}
						variant={MobergButtonVariant.OUTLINE}
						shape={MobergButtonShape.WIDE}
						onClick={handleBack}
					>
						Back
					</MobergButton>

					<MobergButton
						theme={MobergTheme.BLUE}
						variant={MobergButtonVariant.FILLED}
						shape={MobergButtonShape.WIDE}
						onClick={handleNext}
						disabled={uploadProcessProvider.selectedSiteID === '' || uploadProcessProvider.selectedProjectID === ''}
					>
						Next
					</MobergButton>
				</ModalFooter>
			</UploadFilesModal >
		</>
	)
}

export default UploadProjectDetail